import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, Video, FileText } from 'lucide-react';
import { Project } from '../data/portfolioData';

interface ProjectCardProps {
  project: Project;
  onOpen: (project: Project) => void;
  index: number;
}

export const ProjectCard: React.FC<ProjectCardProps> = ({ project, onOpen, index }) => {
  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.6, delay: (index % 3) * 0.1 }}
      className="group relative cursor-pointer"
      onClick={() => onOpen(project)}
    >
      {/* Corner crosshairs on hover */}
      <div className="absolute -top-1.5 -left-1.5 w-3 h-3 border-t border-l border-transparent group-hover:border-arch-accent transition-colors z-20" />
      <div className="absolute -bottom-1.5 -right-1.5 w-3 h-3 border-b border-r border-transparent group-hover:border-arch-accent transition-colors z-20" />

      {/* Main Card Container */}
      <div className="relative aspect-[16/11] rounded-sm overflow-hidden bg-arch-card border border-white/10 group-hover:border-white/30 transition-all duration-500">
        {/* Project Image */}
        <img
          src={project.heroImage}
          alt={project.title}
          loading="lazy"
          className="w-full h-full object-cover grayscale-[20%] group-hover:grayscale-0 group-hover:scale-105 transition-all duration-700 ease-out"
        />

        {/* Gradient Scrim */}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0B0D] via-[#0A0B0D]/30 to-transparent opacity-70 group-hover:opacity-50 transition-opacity" />

        {/* Top Badges */}
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-2">
            <span className="px-2.5 py-1 rounded bg-[#0A0B0D]/85 backdrop-blur border border-white/10 text-[10px] font-mono text-neutral-300 tracking-wider uppercase">
              {project.category}
            </span>
            <span className="px-2 py-1 rounded bg-black/60 backdrop-blur border border-white/5 text-[10px] font-mono text-arch-muted">
              {project.year}
            </span>
          </div>

          <div className="flex items-center gap-1.5">
            {project.videoUrl && (
              <span className="p-1.5 rounded bg-[#0A0B0D]/80 backdrop-blur border border-white/10 text-arch-accent" title="Video Walkthrough Available">
                <Video className="w-3 h-3" />
              </span>
            )}
            {project.blueprintImage && (
              <span className="p-1.5 rounded bg-[#0A0B0D]/80 backdrop-blur border border-white/10 text-arch-cyan" title="Architectural Blueprint Attached">
                <FileText className="w-3 h-3" />
              </span>
            )}
            <div className="w-7 h-7 rounded-full bg-[#0A0B0D]/80 backdrop-blur border border-white/15 flex items-center justify-center text-white group-hover:bg-arch-accent group-hover:text-[#0A0B0D] transition-colors">
              <ArrowUpRight className="w-3.5 h-3.5" />
            </div>
          </div>
        </div>

        {/* Bottom Details */}
        <div className="absolute bottom-0 left-0 right-0 p-5 z-10 space-y-1.5">
          <div className="flex items-center justify-between text-[10px] font-mono text-arch-muted uppercase tracking-wider">
            <span>{project.location}</span>
            <span className="text-arch-accent">{project.area}</span>
          </div>

          <h3 className="text-lg md:text-xl font-serif text-white font-medium group-hover:text-arch-accent transition-colors leading-snug">
            {project.title}
          </h3>

          <p className="text-xs text-neutral-400 line-clamp-2 font-light leading-relaxed">
            {project.shortDescription}
          </p>
        </div>
      </div>

      {/* Footer Technical Bar */}
      <div className="mt-2.5 flex items-center justify-between text-[10px] font-mono text-neutral-500 px-0.5">
        <span className="tracking-widest">NO. {project.id} // {project.specs.structuralSystem.split(' ')[0]}</span>
        <span className="text-neutral-400 group-hover:text-arch-accent transition-colors">
          View Specifications & Gallery →
        </span>
      </div>
    </motion.div>
  );
};
